import { SxProps, Theme } from '@mui/system';
import { useTheme } from '@mui/styles';
import { CfPagination, CfPaginationProps, defaultPageSizeOptions } from '../../../Pagination/CfPagination';
import { CfDataGridForStoreDataProps, CfDataGridProps } from '../../CfDataGridProps';
import { getLastPage, getTotalItemCount } from '../../utility/data-grid-props';

export interface CfDataGridPaginationProps<TItem, TAppState> {
  dataGridProps: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>;
  /**
   * Callback fired when pagination is initialized.
   *
   * @param {number} pageIndex The page index selected.
   * @param {number} pageSize The page size selected.
   */
  onInit: (pageIndex: number, pageSize: number) => void;
  /**
   * Callback fired when pagination is changed.
   *
   * @param {number} pageIndex The page index selected.
   * @param {number} pageSize The page size selected.
   */
  onChange: (pageIndex: number, pageSize: number) => void;
  sx?: SxProps<Theme>;
}

export function CfDataGridPagination<TItem, TAppState>(
  props: CfDataGridPaginationProps<TItem, TAppState>
): JSX.Element {
  const theme = useTheme();

  let pageSizeOptions = defaultPageSizeOptions;
  if (props.dataGridProps.pagination?.pageSizeOptions) {
    pageSizeOptions = props.dataGridProps.pagination.pageSizeOptions;
  }

  const lastPage = getLastPage(props.dataGridProps);
  const totalItemCount = getTotalItemCount(props.dataGridProps);

  const pageIndex = lastPage?.pageIndex ?? 0;
  const paginationProps: CfPaginationProps = {
    pageSize: lastPage?.pageSize ?? 25,
    pageIndex: pageIndex,
    totalItemCount: totalItemCount,
    pageSizeOptions: pageSizeOptions,
  };

  paginationProps.onInit = props.onInit;
  paginationProps.onChange = (pageIndex, pageSize, eventType) => {
    if (eventType === 'pageIndex') {
      const target = props.dataGridProps.componentContainers?.scrollTopTarget ?? document.body;
      window.scrollTo({
        top: target.offsetTop - theme.custom.headerHeights['xs'] - parseInt(theme.spacing(0.5)),
        behavior: 'smooth',
      });
    }
    props.onChange?.(pageIndex, pageSize);
  };

  return <CfPagination sx={props.sx} {...paginationProps} />;
}
